import { useState, useEffect, useRef } from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CheckCircle2,
  AlertCircle,
  Clock,
  RefreshCw,
  Loader2,
  Trash2,
  ArrowRight,
} from "lucide-react";
import { useDocumentStats, usePollDropboxInbox, useCleanupMissingDocuments, useRecoverStaleDocuments, useLastScanTime } from "@/hooks/useDocuments";
import { formatDistanceToNow, startOfToday, endOfToday, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { type DateFilterValue } from "@/components/intake/IntakeDateFilter";
import { NeedsReviewQueue } from "@/components/documents/NeedsReviewQueue";
import { DocumentReviewSheet } from "@/components/documents/DocumentReviewSheet";
import { RoutedTodayDialog } from "@/components/documents/RoutedTodayDialog";
import { NeedsReviewDialog } from "@/components/documents/NeedsReviewDialog";
import { InProgressDialog } from "@/components/documents/InProgressDialog";
import { FailedDocumentsDialog } from "@/components/documents/FailedDocumentsDialog";

// Pre-built date filters for the routing stats banner
const DATE_FILTERS = {
  today: { type: "today", startDate: startOfToday(), endDate: endOfToday() } as DateFilterValue,
  week: { type: "week", startDate: startOfWeek(new Date(), { weekStartsOn: 0 }), endDate: endOfWeek(new Date(), { weekStartsOn: 0 }) } as DateFilterValue,
  month: { type: "month", startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()) } as DateFilterValue,
  quarter: (() => {
    const now = new Date();
    const qMonth = Math.floor(now.getMonth() / 3) * 3;
    return {
      type: "custom" as const,
      startDate: new Date(now.getFullYear(), qMonth, 1),
      endDate: new Date(now.getFullYear(), qMonth + 3, 0, 23, 59, 59, 999),
    } as DateFilterValue;
  })(),
};

// Compact count for the stat tiles: exact below 1,000, then "1.5k", "12k".
// The tile's title attribute carries the exact number.
const formatCount = (n: number) => {
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k < 10 ? k.toFixed(1).replace(/\.0$/, "") : Math.round(k)}k`;
};

export default function Documents() {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [isRoutedDialogOpen, setIsRoutedDialogOpen] = useState(false);
  const [routedDialogFilter, setRoutedDialogFilter] = useState<DateFilterValue>(DATE_FILTERS.today);
  const [isNeedsReviewDialogOpen, setIsNeedsReviewDialogOpen] = useState(false);
  const [isInProgressDialogOpen, setIsInProgressDialogOpen] = useState(false);
  const [isFailedDialogOpen, setIsFailedDialogOpen] = useState(false);
  const { data: stats, isLoading: statsLoading } = useDocumentStats();
  const pollInbox = usePollDropboxInbox();
  const cleanupMissing = useCleanupMissingDocuments();
  const recoverStale = useRecoverStaleDocuments();
  const { data: lastScanTime } = useLastScanTime();

  // Auto-recover stuck documents on page load:
  // 1. Advance docs stuck in 'detected'/'parsing' that already have parse results → 'needs_review'
  // 2. Cleanup docs whose files no longer exist in Dropbox
  const hasRunRecovery = useRef(false);
  useEffect(() => {
    if (!hasRunRecovery.current && !statsLoading && stats) {
      hasRunRecovery.current = true;
      const hasStuck = (stats.detected || 0) + (stats.parsing || 0) > 0;
      if (hasStuck) {
        // Advance stuck docs first, then cleanup missing
        recoverStale.mutate(undefined, {
          onSettled: () => {
            const inProgressCount = (stats.detected || 0) + (stats.parsing || 0) + (stats.routing || 0);
            if (inProgressCount > 0) {
              cleanupMissing.mutate();
            }
          },
        });
      }
    }
  }, [statsLoading, stats]);

  const handleDocumentClick = (documentId: string) => {
    setSelectedDocumentId(documentId);
    setIsDetailSheetOpen(true);
  };

  const openRoutedDialog = (filter: DateFilterValue) => {
    setRoutedDialogFilter(filter);
    setIsRoutedDialogOpen(true);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Documents</h1>
        </div>
        <div className="flex items-center gap-3">
          {lastScanTime && (
            <span className="text-xs text-muted-foreground">
              Last scanned {formatDistanceToNow(new Date(lastScanTime), { addSuffix: true })}
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => cleanupMissing.mutate()}
            disabled={cleanupMissing.isPending}
          >
            {cleanupMissing.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cleaning...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Cleanup Missing
              </>
            )}
          </Button>
          <Button
            onClick={() => pollInbox.mutate()}
            disabled={pollInbox.isPending}
          >
            {pollInbox.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Scan Inbox
              </>
            )}
          </Button>
        </div>
      </header>

      <div className="flex-1 p-6 space-y-6">
        {/* Routing Activity Banner */}
        <Card>
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <h2 className="text-sm font-semibold">Routed Documents</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openRoutedDialog(DATE_FILTERS.today)}
            >
              View Report
              <ArrowRight className="ml-2 h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-4 border-t">
            {([
              { label: "Today", value: stats?.routed_today, filter: DATE_FILTERS.today },
              { label: "This Week", value: stats?.routed_week, filter: DATE_FILTERS.week },
              { label: "This Month", value: stats?.routed_month, filter: DATE_FILTERS.month },
              { label: "This Quarter", value: stats?.routed_quarter, filter: DATE_FILTERS.quarter },
            ] as const).map((item, i) => (
              <button
                key={item.label}
                className={`flex flex-col items-center gap-1 py-4 px-3 hover:bg-muted/50 transition-colors cursor-pointer ${i > 0 ? "border-l" : ""}`}
                onClick={() => openRoutedDialog(item.filter)}
              >
                {statsLoading ? (
                  <Skeleton className="h-8 w-12" />
                ) : (
                  <span className="text-2xl font-bold" title={(item.value ?? 0).toLocaleString()}>
                    {formatCount(item.value ?? 0)}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">{item.label}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Operational Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card
            className="cursor-pointer hover:border-amber-300 hover:shadow-md transition-all group"
            onClick={() => setIsNeedsReviewDialogOpen(true)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Needs Review</CardTitle>
              <AlertCircle className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{(stats?.needs_review || 0) + (stats?.parsed || 0)}</div>
              )}
              <p className="text-xs text-muted-foreground group-hover:text-amber-600 transition-colors">
                View details &rarr;
              </p>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:border-blue-300 hover:shadow-md transition-all group"
            onClick={() => setIsInProgressDialogOpen(true)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">In Progress</CardTitle>
              <Clock className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">
                  {(stats?.detected || 0) + (stats?.parsing || 0) + (stats?.routing || 0)}
                </div>
              )}
              <p className="text-xs text-muted-foreground group-hover:text-blue-600 transition-colors">
                View details &rarr;
              </p>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:border-red-300 hover:shadow-md transition-all group"
            onClick={() => setIsFailedDialogOpen(true)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Failed</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{stats?.failed || 0}</div>
              )}
              <p className="text-xs text-muted-foreground group-hover:text-red-600 transition-colors">
                View details &rarr;
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Needs Review Queue */}
        <NeedsReviewQueue
          onDocumentClick={handleDocumentClick}
          selectedDocumentId={selectedDocumentId}
        />
      </div>

      <DocumentReviewSheet
        open={isDetailSheetOpen}
        onOpenChange={setIsDetailSheetOpen}
        documentId={selectedDocumentId}
      />

      <RoutedTodayDialog
        open={isRoutedDialogOpen}
        onOpenChange={setIsRoutedDialogOpen}
        initialDateFilter={routedDialogFilter}
      />

      <NeedsReviewDialog
        open={isNeedsReviewDialogOpen}
        onOpenChange={setIsNeedsReviewDialogOpen}
        onDocumentReview={(documentId) => {
          setIsNeedsReviewDialogOpen(false);
          setSelectedDocumentId(documentId);
          setIsDetailSheetOpen(true);
        }}
      />

      <InProgressDialog open={isInProgressDialogOpen} onOpenChange={setIsInProgressDialogOpen} />

      <FailedDocumentsDialog open={isFailedDialogOpen} onOpenChange={setIsFailedDialogOpen} />
    </div>
  );
}
